import { useEffect, useMemo, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { siteSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import { useUpdateSiteDetailsMutation, useGetOrgansQuery } from "../../services/api";
import { SingleSelectInput } from "../../components/SelectInput";
import style from "./site.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof siteSchema.__outputType;
type OrganOption = {
  id: number;
  label: string;
  value: number;
};

type EditSiteNavState = {
  site?: Site;
};

/**
 * Edit Site page for updating an existing site.
 *
 * Notes:
 * - The row being edited is handed over in router state by the Sites list, so
 *   this screen does not re-fetch it. Browsers persist history.state, so a
 *   reload still works — but a new tab or a shared link carries no state, which
 *   is why the no-state fallback below sends the user back to the list.
 * - The organ lookup is still fetched because the dropdown needs its options.
 * - Validation is enforced via `siteSchema`.
 */
const EditSite = () => {
  const [updateSite] = useUpdateSiteDetailsMutation();
  const { id } = useParams();
  const { state } = useLocation();
  const { site: siteDetails } = (state ?? {}) as EditSiteNavState;
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const { data: organData, isLoading: isOrganLoading } = useGetOrgansQuery({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: false,
    search_key: "",
  });

  const organs = useMemo<OrganOption[]>(
    () =>
      organData?.items?.map((organ: Organ) => {
        return {
          id: organ?.id,
          label: organ?.name,
          value: organ?.id,
        };
      }) ?? [],
    [organData]
  );

  const navigation = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isSavingSite, setIsSavingSite] = useState(false);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(siteSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<FormState>({
    resolver: yupResolver(siteSchema),
    mode: "onChange",
  });

  const selectedOrganId = watch("organ_id");

  useEffect(() => {
    // Hydrate once the organ lookup has loaded, so the dropdown can resolve the
    // stored organ id to an option.
    if (siteDetails && !isOrganLoading) {
      reset({
        name: siteDetails?.name,
        organ_id: siteDetails?.organ_id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteDetails, isOrganLoading, reset]);

  /**
   * Submits the updated site payload.
   *
   * Args:
   *   values (FormState): Form values for the site update.
   *
   * Returns:
   *   Promise<void>
   *
   * Raises:
   *   Error: Propagated when the update mutation fails.
   *
   * Notes:
   * - Uses route `id`; blocks submit when `id` is invalid.
   */
  const onSubmit = async (values: FormState) => {
    try {
      // Block updates if route id is missing/invalid to avoid unintended mutations.
      const parsedId = parseInt(id ?? "", 10);
      if (Number.isNaN(parsedId)) {
        setMessage({
          type: "error",
          text: "Unable to update site. Please reload and try again.",
        });
        return;
      }
      setIsSavingSite(true);
      const { name, organ_id } = values;
      const payload: EditSitePayload = {
        id: parsedId,
        name: name,
        organ_id: organ_id,
      };

      await updateSite(payload).unwrap();
      setIsSavingSite(false);
      setMessage({
        type: "success",
        text: "Site updated successfully.",
      });
      navigationTimeoutRef.current = setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
      setIsSavingSite(false);
    }
  };
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  if (!siteDetails) {
    return (
      <Box className={style.container}>
        <Typography className={style.detailHeading} sx={{ marginTop: "10px" }}>
          This site is no longer available
        </Typography>
        <Typography sx={{ color: colors.grey, marginTop: "10px" }}>
          Site details are carried over from the list screen, so this page
          cannot be opened directly or shared as a link. Open it again from
          Sites.
        </Typography>
        <Box className={style.buttons}>
          <Button
            sx={{ width: "200px", height: "55px" }}
            variant="contained"
            onClick={() => navigation("/sites")}
            label={"Back to Sites"}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {isOrganLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              width: "calc(100vw - 400px)",
              zIndex: 999,
              backgroundColor: "transparent",
              position: "absolute",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <Box>
          <Typography
            className={style.detailHeading}
            sx={{ marginTop: "10px" }}
          >
            Site Details
          </Typography>
          <Box className={style.flexTop}>
            <Box className={style.row}>
              <Input
                placeholder="e.g Upper Lobe"
                label="Name"
                required
                register={register}
                name="name"
                errors={errors}
              />
            </Box>
            <Box className={style.row}>
              <SingleSelectInput
                placeholder="Select organ"
                label="Organ"
                required
                register={register}
                name="organ_id"
                errors={errors}
                data={organs}
                value={selectedOrganId ?? ""}
              />
            </Box>
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
              }}
              variant="outlined"
              onClick={() => navigation(-1)}
              label={"Back"}
            />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label={"Save"}
              loading={isSavingSite}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default EditSite;
